import { useState, useEffect, useRef } from "react";

export default function BeatsSimulation() {
  const [f1, setF1] = useState(5);
  const [f2, setF2] = useState(7);
  const [playing, setPlaying] = useState(false);
  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const osc1Ref = useRef(null);
  const osc2Ref = useRef(null);
  const animRef = useRef(null);
  const timeRef = useRef(0);

  const beatFreq = Math.abs(f1 - f2);
  const avgFreq = ((f1 + f2) / 2).toFixed(1);

  // Draw waves on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    let animId;
    let t = 0;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      // Background
      ctx.fillStyle = "#0a0a1a";
      ctx.fillRect(0, 0, W, H);

      // Grid lines
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.lineWidth = 1;
      for (let y = 0; y < H; y += H / 6) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }

      const drawWave = (freq, color, yCenter, label) => {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        for (let x = 0; x < W; x++) {
          const xVal = (x / W) * 4 * Math.PI;
          const y = yCenter + Math.sin(freq * xVal - t) * (H / 8);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.stroke();
        ctx.fillStyle = color;
        ctx.font = "12px monospace";
        ctx.fillText(label, 10, yCenter - H / 8 - 6);
      };

      // Wave 1
      drawWave(f1, "#00e5ff", H * 0.2, `Wave 1: f₁ = ${f1} Hz`);

      // Wave 2
      drawWave(f2, "#ff4081", H * 0.45, `Wave 2: f₂ = ${f2} Hz`);

      // Resultant (beats)
      ctx.beginPath();
      ctx.strokeStyle = "#ffd600";
      ctx.lineWidth = 2.5;
      for (let x = 0; x < W; x++) {
        const xVal = (x / W) * 4 * Math.PI;
        const y1 = Math.sin(f1 * xVal - t);
        const y2 = Math.sin(f2 * xVal - t);
        const resultant = (y1 + y2) / 2;
        const y = H * 0.78 + resultant * (H / 7);
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Envelope
      ctx.beginPath();
      ctx.strokeStyle = "rgba(255,214,0,0.3)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      for (let x = 0; x < W; x++) {
        const xVal = (x / W) * 4 * Math.PI;
        const envelope = Math.abs(Math.cos(((f2 - f1) / 2) * xVal - t * 0.5));
        const y = H * 0.78 - envelope * (H / 7);
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.beginPath();
      for (let x = 0; x < W; x++) {
        const xVal = (x / W) * 4 * Math.PI;
        const envelope = Math.abs(Math.cos(((f2 - f1) / 2) * xVal - t * 0.5));
        const y = H * 0.78 + envelope * (H / 7);
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = "#ffd600";
      ctx.font = "12px monospace";
      ctx.fillText(`Resultant (Beats): fbeat = |f₁ - f₂| = ${beatFreq} Hz`, 10, H * 0.63);

      t += 0.04;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animId);
  }, [f1, f2, beatFreq]);

  const toggleSound = () => {
    if (playing) {
      osc1Ref.current?.stop();
      osc2Ref.current?.stop();
      audioCtxRef.current?.close();
      setPlaying(false);
    } else {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const o1 = ctx.createOscillator();
      const o2 = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0.3;
      o1.frequency.value = 220 + f1 * 10;
      o2.frequency.value = 220 + f2 * 10;
      o1.connect(gain); o2.connect(gain); gain.connect(ctx.destination);
      o1.start(); o2.start();
      osc1Ref.current = o1;
      osc2Ref.current = o2;
      setPlaying(true);
    }
  };

  useEffect(() => {
    if (playing && osc1Ref.current && osc2Ref.current) {
      osc1Ref.current.frequency.value = 220 + f1 * 10;
      osc2Ref.current.frequency.value = 220 + f2 * 10;
    }
  }, [f1, f2, playing]);

  useEffect(() => {
    return () => {
      osc1Ref.current?.stop();
      audioCtxRef.current?.close();
    };
  }, []);

  return (
    <div style={{
      background: "#0a0a1a",
      minHeight: "100vh",
      color: "white",
      fontFamily: "'Courier New', monospace",
      padding: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    }}>
      {/* Title */}
      <div style={{ textAlign: "center" }}>
        <h1 style={{
          fontSize: "1.8rem",
          background: "linear-gradient(90deg, #00e5ff, #ff4081, #ffd600)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          marginBottom: "6px",
        }}>⚡ Beats Phenomenon</h1>
        <p style={{ color: "#aaa", fontSize: "0.9rem" }}>
          Superposition of two waves with close frequencies
        </p>
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={800}
        height={340}
        style={{
          width: "100%",
          borderRadius: "12px",
          border: "1px solid rgba(255,255,255,0.1)",
          background: "#0a0a1a",
        }}
      />

      {/* Controls */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
      }}>
        {/* f1 slider */}
        <div style={{ background: "rgba(0,229,255,0.08)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(0,229,255,0.2)" }}>
          <label style={{ color: "#00e5ff", fontSize: "0.9rem" }}>
            Wave 1 Frequency: <strong>{f1} Hz</strong>
          </label>
          <input
            type="range" min="1" max="20" value={f1}
            onChange={(e) => setF1(Number(e.target.value))}
            style={{ width: "100%", marginTop: "10px", accentColor: "#00e5ff" }}
          />
        </div>

        {/* f2 slider */}
        <div style={{ background: "rgba(255,64,129,0.08)", borderRadius: "12px", padding: "16px", border: "1px solid rgba(255,64,129,0.2)" }}>
          <label style={{ color: "#ff4081", fontSize: "0.9rem" }}>
            Wave 2 Frequency: <strong>{f2} Hz</strong>
          </label>
          <input
            type="range" min="1" max="20" value={f2}
            onChange={(e) => setF2(Number(e.target.value))}
            style={{ width: "100%", marginTop: "10px", accentColor: "#ff4081" }}
          />
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "12px",
      }}>
        {[
          { label: "f₁", value: `${f1} Hz`, color: "#00e5ff" },
          { label: "f₂", value: `${f2} Hz`, color: "#ff4081" },
          { label: "Beat Frequency |f₁ - f₂|", value: `${beatFreq} Hz`, color: "#ffd600" },
        ].map((s) => (
          <div key={s.label} style={{
            background: "rgba(255,255,255,0.04)",
            borderRadius: "10px",
            padding: "14px",
            textAlign: "center",
            border: `1px solid ${s.color}33`,
          }}>
            <div style={{ color: s.color, fontSize: "0.8rem", marginBottom: "6px" }}>{s.label}</div>
            <div style={{ color: "white", fontSize: "1.4rem", fontWeight: "bold" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Sound button */}
      <div style={{ textAlign: "center" }}>
        <button
          onClick={toggleSound}
          style={{
            background: playing ? "#ff4081" : "#00e5ff",
            color: "#0a0a1a",
            border: "none",
            padding: "12px 32px",
            borderRadius: "30px",
            fontSize: "1rem",
            fontWeight: "bold",
            cursor: "pointer",
            fontFamily: "monospace",
          }}
        >
          {playing ? "🔇 Stop Sound" : "🔊 Hear the Beats"}
        </button>
      </div>

      {/* Formula */}
      <div style={{
        background: "rgba(255,214,0,0.06)",
        border: "1px solid rgba(255,214,0,0.2)",
        borderRadius: "12px",
        padding: "16px 20px",
      }}>
        <p style={{ color: "#ffd600", marginBottom: "8px", fontWeight: "bold" }}>📐 Formula</p>
        <p style={{ color: "#ddd", fontSize: "0.9rem", lineHeight: "1.8" }}>
          y₁ = A sin(2πf₁t) &nbsp;|&nbsp; y₂ = A sin(2πf₂t)<br />
          y = y₁ + y₂ = <span style={{ color: "#ffd600" }}>2A cos(π(f₁-f₂)t)</span> · sin(π(f₁+f₂)t)<br />
          Beat Frequency = <span style={{ color: "#ffd600" }}>|f₁ - f₂| = {beatFreq} Hz</span>
        </p>
      </div>
    </div>
  );
}